
import { Eye, Plus, Check, Loader2, Calendar, X } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { formatDistanceToNow } from 'date-fns'
import { TeamLogo } from './team-logo'
import { getTeamShortDescription } from '../../utils/teamDescriptions'
import type { Team } from '../../types'

export interface SpotButtonState {
  text: string
  variant: 'default' | 'secondary' | 'outline'
  disabled: boolean
  loading: boolean
  isSpotted: boolean
}

interface CleanTeamCardProps {
  team: Team
  isSpotted: boolean
  onSpot: (teamId: string) => void
  onViewDetails: (team: Team) => void
  spotButtonState: SpotButtonState
  showSpottedIndicator?: boolean
  spottedAt?: Date
  spottedMode?: 'all-time' | 'trip-mode'
  onRemove?: (team: Team) => void
  isRemoving?: boolean
  className?: string
  showDescription?: boolean
  showFoundedYear?: boolean
  showRemoveButton?: boolean
}

export function CleanTeamCard({
  team,
  isSpotted,
  onSpot,
  onViewDetails,
  spotButtonState,
  showSpottedIndicator = true,
  spottedAt,
  spottedMode,
  onRemove,
  isRemoving = false,
  className,
  showDescription = false,
  showFoundedYear = false,
  showRemoveButton = true
}: CleanTeamCardProps) {
  return (
    <Card className={`hover:shadow-lg active:shadow-xl transition-shadow min-h-[160px] sm:min-h-[150px] touch-manipulation ${className || ''}`}>
      <CardHeader 
        className="cursor-pointer hover:bg-muted/50 active:bg-muted/70 transition-colors touch-manipulation"
        onClick={() => onViewDetails(team)}
      >
        <div className="flex items-start space-x-3 sm:space-x-3">
          {/* Team Logo - optimized sizes for mobile */}
          <div className="flex-shrink-0">
            <TeamLogo 
              team={team} 
              size={team.sport === 'College' ? 44 : 52} 
              className="rounded-lg border border-gray-200 shadow-sm" 
            />
          </div>
          
          <div className="flex-1 min-w-0">
            {/* Team name with better mobile sizing */}
            <div className="mb-2">
              <CardTitle className="text-lg sm:text-lg leading-tight font-semibold" 
              style={{
                // Better text display for mobile - allow more space
                overflow: 'visible',
                textOverflow: 'unset',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: '1.3'
              }}>
                {team.name}
              </CardTitle>
            </div>
            
            {/* League badge and location with improved mobile layout */}
            <div className="space-y-2">
              <Badge variant="outline" className="text-xs font-medium px-2 py-1">
                {team.league}
              </Badge>
              <div className="text-sm text-gray-600 leading-relaxed">
                <div className="font-medium">{team.city}</div>
                {team.state && (
                  <div className="text-gray-500 text-xs mt-0.5">
                    {team.state}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Remove button - larger touch target for mobile */}
          {onRemove && showRemoveButton && (
            <div className="flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemove(team)
                }}
                disabled={isRemoving}
                className="text-destructive hover:text-destructive hover:bg-destructive/10 active:bg-destructive/20 h-10 w-10 sm:h-8 sm:w-8 p-0 touch-manipulation"
                title={`Remove ${team.name} from spots`}
              >
                {isRemoving ? (
                  <div className="h-5 w-5 sm:h-4 sm:w-4 animate-spin rounded-full border-2 border-current border-r-transparent" />
                ) : (
                  <X className="h-5 w-5 sm:h-4 sm:w-4" />
                )}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="pt-2">
        <div className="space-y-3">
          {/* Team info with better mobile spacing */}
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <Badge variant="outline" className="text-xs font-medium px-2 py-1">
              {team.sport}
            </Badge>
            {showFoundedYear && (
              <span className="text-xs text-gray-500">Founded: {team.founded}</span>
            )}
          </div>

          {/* Spotted date for spotted teams */}
          {spottedAt && (
            <div className="flex items-center space-x-2 text-sm text-gray-600 bg-green-50 p-2 rounded-lg">
              <Calendar className="h-4 w-4 text-green-600 flex-shrink-0" />
              <span>Spotted {formatDistanceToNow(spottedAt, { addSuffix: true })}</span>
            </div>
          )}

          {/* Team description - only show if enabled */}
          {showDescription && (
            <p className="text-sm text-gray-600 leading-relaxed">
              {getTeamShortDescription(team)}
            </p>
          )}

          {/* Action buttons - Enhanced for mobile with better touch targets */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
            <Button
              onClick={(e) => {
                e.stopPropagation()
                onViewDetails(team)
              }}
              variant="outline"
              className="flex-1 min-w-0 h-11 sm:h-10 touch-manipulation active:scale-95 transition-transform"
            >
              <Eye className="h-5 w-5 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
              <span className="font-medium">View Details</span>
            </Button>
            
            {/* Only show spot button if not in spotted teams view and not already spotted */}
            {!onRemove && !isSpotted && (
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  onSpot(team.id)
                }}
                disabled={spotButtonState.disabled}
                className="flex-1 min-w-0 h-11 sm:h-10 touch-manipulation active:scale-95 transition-transform"
                variant={spotButtonState.variant}
              >
                {spotButtonState.loading ? (
                  <Loader2 className="h-5 w-5 sm:h-4 sm:w-4 mr-2 flex-shrink-0 animate-spin" />
                ) : (
                  <Plus className="h-5 w-5 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                )}
                <span className="font-medium">
                  {spotButtonState.loading ? 'Spotting...' : 'Spot Team'}
                </span>
              </Button>
            )}
          </div>

          {/* Single spotted indicator at bottom - enhanced styling */}
          {showSpottedIndicator && isSpotted && (
            <div className="pt-3 border-t border-gray-100">
              <div className="flex items-center justify-center">
                <Badge 
                  variant={spottedMode === 'trip-mode' ? "secondary" : "default"}
                  className="flex items-center space-x-1 px-3 py-1"
                >
                  <Check className="h-4 w-4" />
                  <span className="font-medium">
                    {spottedMode === 'trip-mode' ? 'Trip Spot' : 'Spotted'}
                  </span>
                </Badge>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// Specialized version for spotted teams
export function SpottedTeamCard({
  team,
  spottedAt,
  spottedMode = 'all-time',
  onViewDetails,
  onRemove,
  isRemoving = false,
  className,
  showRemoveButton = true
}: {
  team: Team & { spottedAt: Date; mode?: 'all-time' | 'trip-mode' }
  spottedAt: Date
  spottedMode?: 'all-time' | 'trip-mode'
  onViewDetails: (team: Team) => void
  onRemove: (team: Team) => void
  isRemoving?: boolean
  className?: string
  showRemoveButton?: boolean
}) {
  return (
    <CleanTeamCard
      team={team}
      isSpotted={true}
      onSpot={() => {}} // No spot action for spotted teams
      onViewDetails={onViewDetails}
      spotButtonState={{
        text: 'Spotted',
        variant: 'secondary',
        disabled: true,
        loading: false,
        isSpotted: true
      }}
      showSpottedIndicator={false}
      spottedAt={spottedAt}
      spottedMode={spottedMode}
      onRemove={onRemove}
      isRemoving={isRemoving}
      className={className}
      showRemoveButton={showRemoveButton}
    />
  )
}